// src/components/Configurator/utils/sanitaryCornerUtils.js

import { OBJECT_TYPES, SANITARY_ELEMENT_TYPES, GRID_CELL_SIZE_M } from '../configuratorConstants';
import { subtractVectors, addVectors, rotatePoint, distance } from '../configuratorUtils';

/**
 * Определяет углы санитарного модуля на основе его стыков
 * @param {Object} module - санитарный модуль
 * @param {Array} allJoints - все стыки в проекте
 * @returns {Array} массив углов с информацией о сэндвичах
 */
export function getSanitaryModuleCorners(module, allJoints) {
  console.log('DEBUG: getSanitaryModuleCorners called', {
    moduleId: module?.id,
    moduleSubtype: module?.subtype,
    allJointsCount: allJoints?.length
  });

  if (!module || module.subtype !== 'sanitary' || !allJoints) {
    console.log('DEBUG: Early return - invalid module or no joints');
    return [];
  }

  // Находим все стыки, связанные с этим модулем
  const moduleJoints = allJoints.filter(joint => 
    joint.moduleA_id === module.id || joint.moduleB_id === module.id
  );

  console.log('DEBUG: Module joints found:', moduleJoints.length);

  if (moduleJoints.length < 1) {
    console.log('DEBUG: No joints found for module');
    return [];
  }

  // Группируем стыки по углам
  const corners = [];
  const tolerance = 0.5; // допуск для определения углов

  // 1. Ищем углы между стыками (joint-to-joint)
  moduleJoints.forEach(joint => {
    const jointEndpoints = getJointEndpoints(joint);
    
    moduleJoints.forEach(otherJoint => {
      if (otherJoint.id === joint.id) return;
      
      const otherEndpoints = getJointEndpoints(otherJoint);
      
      // Проверяем, образуют ли стыки угол (имеют общую точку)
      const commonPoint = findCommonPoint(jointEndpoints, otherEndpoints, tolerance);
      
      if (commonPoint) {
        // Проверяем, не добавлен ли уже этот угол
        const existingCorner = corners.find(corner => 
          isSameCorner(corner, joint, otherJoint, commonPoint, tolerance)
        );
        
        if (!existingCorner) {
          corners.push({
            id: `corner_${joint.id}_${otherJoint.id}`,
            point: commonPoint,
            joints: [joint, otherJoint],
            sandwiches: [
              { jointId: joint.id, side: 1, elements: [] },
              { jointId: joint.id, side: -1, elements: [] },
              { jointId: otherJoint.id, side: 1, elements: [] },
              { jointId: otherJoint.id, side: -1, elements: [] }
            ]
          });
        }
      }
    });
  });

  // 2. Ищем углы между стыками и стенами (joint-to-wall)
  moduleJoints.forEach(joint => {
    const jointEndpoints = getJointEndpoints(joint);
    
    console.log('DEBUG: Checking joint-to-wall corners for joint:', joint.id, {
      jointEndpoints,
      jointPosition: { x: joint.x, y: joint.y, width: joint.width, rotation: joint.rotation }
    });
    
    // Проверяем стены модуля, которые могут образовывать угол со стыком
    const moduleWalls = module.wallSegments || {};
    const moduleWallKeys = Object.keys(moduleWalls);
    
    console.log('DEBUG: Module walls:', moduleWallKeys);
    
    moduleWallKeys.forEach(wallKey => {
      const wallSegment = moduleWalls[wallKey];
      if (wallSegment && wallSegment.elements) {
        // Парсим координаты стены
        const [coords, orientation] = wallKey.split('_');
        const [x, y] = coords.split(',').map(Number);
        
        // Вычисляем конечные точки стены
        let wallEndpoints;
        if (orientation === 'h') {
          wallEndpoints = [
            { x, y },
            { x: x + 1, y }
          ];
        } else {
          wallEndpoints = [
            { x, y },
            { x, y: y + 1 }
          ];
        }
        
        console.log('DEBUG: Checking wall:', wallKey, {
          wallEndpoints,
          wallSegment: wallSegment.elements.length > 0 ? 'has elements' : 'no elements'
        });
        
        // Проверяем, образует ли стена угол со стыком
        const wallEndpointsObj = { p1: wallEndpoints[0], p2: wallEndpoints[1] };
        const commonPoint = findCommonPoint(jointEndpoints, wallEndpointsObj, tolerance);
        
        console.log('DEBUG: Common point found:', commonPoint ? 'YES' : 'NO', {
          jointEndpoints,
          wallEndpointsObj,
          tolerance,
          distances: [
            { jointP1_to_wallP1: distance(jointEndpoints.p1, wallEndpointsObj.p1) },
            { jointP1_to_wallP2: distance(jointEndpoints.p1, wallEndpointsObj.p2) },
            { jointP2_to_wallP1: distance(jointEndpoints.p2, wallEndpointsObj.p1) },
            { jointP2_to_wallP2: distance(jointEndpoints.p2, wallEndpointsObj.p2) }
          ]
        });
        
        if (commonPoint) {
          // Проверяем, не добавлен ли уже этот угол
          const existingCorner = corners.find(corner => 
            isSameCornerWithWall(corner, joint, wallKey, commonPoint, tolerance)
          );
          
          if (!existingCorner) {
            console.log('DEBUG: Adding new joint-wall corner:', {
              jointId: joint.id,
              wallKey,
              commonPoint
            });
            
            corners.push({
              id: `corner_${joint.id}_wall_${wallKey}`,
              point: commonPoint,
              joints: [joint],
              walls: [wallKey],
              sandwiches: [
                { jointId: joint.id, side: 1, elements: [] },
                { jointId: joint.id, side: -1, elements: [] },
                { wallKey: wallKey, side: 1, elements: [] },
                { wallKey: wallKey, side: -1, elements: [] }
              ]
            });
          } else {
            console.log('DEBUG: Corner already exists:', existingCorner.id);
          }
        }
      }
    });
  });

  // Заполняем информацию об элементах для каждого сэндвича
  corners.forEach(corner => {
    corner.sandwiches.forEach(sandwich => {
      if (sandwich.jointId) {
        // Сэндвич стыка
        const joint = allJoints.find(j => j.id === sandwich.jointId);
        if (joint && joint.elements) {
          sandwich.elements = joint.elements.filter(el => 
            SANITARY_ELEMENT_TYPES.includes(el.type) && 
            el.placementSide === sandwich.side
          );
        }
      } else if (sandwich.wallKey) {
        // Сэндвич стены
        const wallSegment = module.wallSegments[sandwich.wallKey];
        if (wallSegment && wallSegment.elements) {
          sandwich.elements = wallSegment.elements.filter(el => 
            SANITARY_ELEMENT_TYPES.includes(el.type) && 
            el.placementSide === sandwich.side
          );
        }
      }
    });
  });

  return corners;
}

/**
 * Получает конечные точки стыка
 */
function getJointEndpoints(joint) {
  const center = { x: joint.x, y: joint.y };
  const halfWidth = joint.width / 2;
  const rotation = joint.rotation || 0;
  
  const angle = (rotation * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  
  return {
    p1: {
      x: center.x - halfWidth * cos,
      y: center.y - halfWidth * sin
    },
    p2: {
      x: center.x + halfWidth * cos,
      y: center.y + halfWidth * sin
    }
  };
}

/**
 * Находит общую точку между двумя парами точек
 */
function findCommonPoint(endpoints1, endpoints2, tolerance) {
  const points1 = [endpoints1.p1, endpoints1.p2];
  const points2 = [endpoints2.p1, endpoints2.p2];
  
  for (const p1 of points1) {
    for (const p2 of points2) {
      if (distance(p1, p2) < tolerance) {
        return p1;
      }
    }
  }
  return null;
}

/**
 * Проверяет, является ли угол тем же самым
 */
function isSameCorner(existingCorner, joint1, joint2, point, tolerance) {
  return existingCorner.joints.some(j => j.id === joint1.id || j.id === joint2.id) &&
         distance(existingCorner.point, point) < tolerance;
}

/**
 * Проверяет, является ли угол стык-стена тем же самым
 */
function isSameCornerWithWall(existingCorner, joint, wallKey, point, tolerance) {
  return existingCorner.joints.some(j => j.id === joint.id) &&
         existingCorner.walls && existingCorner.walls.includes(wallKey) &&
         distance(existingCorner.point, point) < tolerance;
}


/**
 * Проверяет, можно ли разместить санитарный элемент в указанном сэндвиче
 * @param {Object} cornersMap - карта углов по ID модулей
 * @param {string} jointId - ID стыка
 * @param {number} side - сторона стыка (1 или -1)
 * @param {string} elementType - тип элемента для размещения
 * @returns {boolean} можно ли разместить элемент
 */
export function canPlaceSanitaryElementInSandwich(cornersMap, jointId, side, elementType, allJoints = [], allModules = []) {
  console.log('DEBUG: canPlaceSanitaryElementInSandwich called', {
    cornersMap: Object.keys(cornersMap || {}),
    jointId,
    side,
    elementType
  });

  if (!SANITARY_ELEMENT_TYPES.includes(elementType)) {
    console.log('DEBUG: Early return - not sanitary element');
    return false;
  }

  // Проверяем, является ли стык между санитарным и жилым модулями
  const joint = allJoints.find(j => j.id === jointId);
  if (joint) {
    const moduleA = allModules.find(m => m.id === joint.moduleA_id);
    const moduleB = allModules.find(m => m.id === joint.moduleB_id);
    
    const isSanitaryToResidential = (moduleA?.subtype === 'sanitary' && moduleB?.subtype === 'residential') ||
                                   (moduleA?.subtype === 'residential' && moduleB?.subtype === 'sanitary');
    
    if (isSanitaryToResidential) {
      console.log('DEBUG: Joint is between sanitary and residential modules, ALLOWING placement');
      return true;
    }
  }

  // Ищем угол, который содержит указанный стык
  let targetCorner = null;
  Object.values(cornersMap).forEach(corners => {
    corners.forEach(corner => {
      if (corner.joints.some(j => j.id === jointId)) {
        targetCorner = corner;
      }
    });
  });

  console.log('DEBUG: Target corner found:', targetCorner ? 'YES' : 'NO');
  if (targetCorner) {
    console.log('DEBUG: Target corner details:', {
      id: targetCorner.id,
      joints: targetCorner.joints.map(j => j.id),
      walls: targetCorner.walls || [],
      sandwiches: targetCorner.sandwiches.map(s => ({
        jointId: s.jointId,
        wallKey: s.wallKey,
        side: s.side,
        elementsCount: s.elements.length
      }))
    });
  }

  // РАЗРЕШАЕМ размещение везде - убираем все ограничения на углы
  console.log('DEBUG: Allowing placement on any joint/sandwich - no corner restrictions');
  return true;
}

/**
 * Получает информацию об углах для всех санитарных модулей
 * @param {Array} modules - все модули
 * @param {Array} allJoints - все стыки
 * @returns {Object} карта углов по ID модулей
 */
export function getAllSanitaryModuleCorners(modules, allJoints) {
  const sanitaryModules = modules.filter(m => m.subtype === 'sanitary');
  const cornersMap = {};
  
  sanitaryModules.forEach(module => {
    cornersMap[module.id] = getSanitaryModuleCorners(module, allJoints);
  });
  
  return cornersMap;
}

/**
 * Проверяет, является ли стык санитарным (между санитарным и жилым модулями)
 * @param {Object} joint - стык для проверки
 * @param {Array} allModules - все модули в проекте
 * @returns {boolean} является ли стык санитарным
 */
export function isSanitaryJoint(joint, allModules) {
  if (!joint || !allModules) return false;
  
  const moduleA = allModules.find(m => m.id === joint.moduleA_id);
  const moduleB = allModules.find(m => m.id === joint.moduleB_id);
  
  const isSanitaryToResidential = (moduleA?.subtype === 'sanitary' && moduleB?.subtype === 'residential') ||
                                 (moduleA?.subtype === 'residential' && moduleB?.subtype === 'sanitary');
  
  return isSanitaryToResidential;
}

/**
 * Проверяет, является ли сегмент крайним в санитарном стыке
 * @param {Object} joint - стык
 * @param {number} segmentIndex - индекс сегмента (0-based)
 * @param {number} placementSide - сторона размещения (1 или -1)
 * @param {Array} allJoints - все стыки для определения соседних стыков
 * @returns {boolean} является ли сегмент крайним
 */
export function isEdgeSegmentInSanitaryJoint(joint, segmentIndex, placementSide, allJoints) {
  if (!joint) return false;
  
  const numSlots = Math.max(1, Math.round(joint.width / GRID_CELL_SIZE_M));
  
  // Если стык состоит из одного сегмента, он не может быть крайним
  if (numSlots <= 1) return false;
  
  // Просто запрещаем размещение на первом и последнем сегментах
  // (самый верхний и самый нижний сегменты)
  const isEdgeSegment = segmentIndex === 0 || segmentIndex === numSlots - 1;
  
  return isEdgeSegment;
}

/**
 * Обновляет информацию об элементах в углах после изменения
 * @param {Object} cornersMap - карта углов
 * @param {Array} allJoints - все стыки
 */
export function updateCornersElements(cornersMap, allJoints, modules) {
  Object.keys(cornersMap).forEach(moduleId => {
    const corners = cornersMap[moduleId];
    const module = modules.find(m => m.id === moduleId);
    
    corners.forEach(corner => {
      corner.sandwiches.forEach(sandwich => {
        if (sandwich.jointId) {
          // Сэндвич стыка
          const joint = allJoints.find(j => j.id === sandwich.jointId);
          if (joint && joint.elements) {
            sandwich.elements = joint.elements.filter(el => 
              SANITARY_ELEMENT_TYPES.includes(el.type) && 
              el.placementSide === sandwich.side
            );
          }
        } else if (sandwich.wallKey && module) {
          // Сэндвич стены
          const wallSegment = module.wallSegments[sandwich.wallKey];
          if (wallSegment && wallSegment.elements) {
            sandwich.elements = wallSegment.elements.filter(el => 
              SANITARY_ELEMENT_TYPES.includes(el.type) && 
              el.placementSide === sandwich.side
            );
          }
        }
      });
    });
  });
}
